import { Badge } from "@/components/ui/Badge";

// Phase 4j portal-polish — maps an `orders.status` value to a
// human-readable label + brand-aligned color. Used on the dashboard
// cards (top-right corner) and on the order-detail hero.
//
// The status set mirrors `app/models/order.rb#aasm` states. Unknown
// statuses fall back to a tokenized human label (`exotic_state` →
// "Exotic state") rather than rendering the raw machine name —
// surfaces a regression as a visual quirk, not a crash.
type Tone = "neutral" | "info" | "success" | "warning" | "danger";

interface StatusEntry {
  label: string;
  tone: Tone;
}

const STATUS_MAP: Record<string, StatusEntry> = {
  reserved: { label: "Reserved", tone: "info" },
  pending_payment: { label: "Pending payment", tone: "warning" },
  pending_procurement: { label: "Sourcing", tone: "warning" },
  pending_id_capture: { label: "ID verification needed", tone: "warning" },
  dispatched: { label: "Dispatched", tone: "info" },
  delivered: { label: "Delivered", tone: "success" },
  on_rent: { label: "On rent", tone: "success" },
  returned: { label: "Returned", tone: "neutral" },
  closed: { label: "Closed", tone: "neutral" },
  cancelled: { label: "Cancelled", tone: "neutral" },
  delivery_failed: { label: "Delivery failed", tone: "danger" },
  capture_failed: { label: "Payment failed", tone: "danger" }
};

const TONE_VARIANT: Record<Tone, "default" | "success" | "warning" | "muted" | "danger"> = {
  neutral: "muted",
  info: "default",
  success: "success",
  warning: "warning",
  danger: "danger"
};

export function StatusPill({ status }: { status: string }): JSX.Element {
  const entry = STATUS_MAP[status] ?? { label: humanize(status), tone: "neutral" as const };
  return (
    <Badge
      role="status"
      variant={TONE_VARIANT[entry.tone]}
      data-testid="status-pill"
      data-status={status}
    >
      {entry.label}
    </Badge>
  );
}

function humanize(raw: string): string {
  if (!raw) return "Unknown";
  const spaced = raw.replace(/[_-]+/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}
